/* eslint-disable no-nested-ternary */
/* eslint-disable react/destructuring-assignment */
import React from 'react';
import TimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en';
import { FormattedMessage } from 'react-intl';
import DateFormatter from 'Common/Components/DateFormatter';
import Skeleton from 'Common/Components/Skeleton';
import {
  ColKbDescription, ColKbReferences,
  ColKbResolution, ColKbPolicyViolations, ColKbCreator,
  ColKbCreatedDate, Col
} from './styled';
import { CheckHeader } from './CheckHeader/CheckHeader';
import { CheckColum } from './CheckColumn/CheckColum';
import NameCell from './NameCell';
import { SeverityHeader } from 'Screens/Contextualization/Manage/components/ManageTable/components/Table/columns';

TimeAgo.addLocale(en);

const BASE_FIXED_COLUMNS = {
  selected: 38,
  severity: 45
};

const FLEXIBLE_COLUMNS = ['name', 'description', 'references', 'resolution', 'policyviolations'];

const getDefaultWidth = (field = {}, fallback = 150) => Math.max(field.minWidth || 0, field.defaultMin || 0) || fallback;

const getColumnWidths = (fields, tableWidth) => {
  const creatorWidth = getDefaultWidth(fields.creator_id, 100);
  const createDateWidth = getDefaultWidth(fields.create_date, 170);
  const widths = {};

  FLEXIBLE_COLUMNS.forEach((column) => {
    widths[column] = getDefaultWidth(fields[column], 150);
  });

  if (!tableWidth) return widths;

  const visibleFlexColumns = FLEXIBLE_COLUMNS.filter((column) => fields[column]?.visible);
  if (!visibleFlexColumns.length) return widths;

  const fixedWidth = BASE_FIXED_COLUMNS.selected + BASE_FIXED_COLUMNS.severity + creatorWidth + createDateWidth;
  const availableWidthForFlex = tableWidth - fixedWidth;
  const currentFlexTotal = visibleFlexColumns.reduce((total, column) => total + widths[column], 0);
  let extraSpace = availableWidthForFlex - currentFlexTotal;

  if (extraSpace <= 0) return widths;

  let growableColumns = visibleFlexColumns.filter((column) => {
    const maxWidth = fields[column]?.maxWidth || Number.POSITIVE_INFINITY;
    return widths[column] < maxWidth;
  });

  while (extraSpace > 0 && growableColumns.length > 0) {
    const delta = extraSpace / growableColumns.length;
    let consumed = 0;

    growableColumns.forEach((column) => {
      const maxWidth = fields[column]?.maxWidth || Number.POSITIVE_INFINITY;
      const growAmount = Math.min(delta, maxWidth - widths[column]);
      widths[column] += growAmount;
      consumed += growAmount;
    });

    if (consumed < 1) break;
    extraSpace -= consumed;
    growableColumns = growableColumns.filter((column) => {
      const maxWidth = fields[column]?.maxWidth || Number.POSITIVE_INFINITY;
      return widths[column] < maxWidth;
    });
  }

  Object.keys(widths).forEach((column) => {
    widths[column] = Math.round(widths[column]);
  });

  return widths;
};

export default function getColumns (isFetching, fields, tableWidth) {
  const widths = getColumnWidths(fields, tableWidth);

  // Severity column is copy/paste from manage. It should be refactored
  const columns = [{
    Header: () => <CheckHeader />,
    id: 'selected',
    accessor: 'Selected',
    show: true,
    sortable: false,
    resizable: true,
    width: BASE_FIXED_COLUMNS.selected,
    maxWidth: BASE_FIXED_COLUMNS.selected,
    minWidth: BASE_FIXED_COLUMNS.selected,
    style: { display: 'flex' },
    Cell: (rowInfo) => ((isFetching) ? <Skeleton /> : <CheckColum template={ rowInfo.original } />),
    custom_order: -1
  }, {
    Header: () => <SeverityHeader />,
    headerClassName: 'hideSortIcon',
    headerStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
    accessor: 'exploitation',
    show: fields.exploitation.visible,
    width: BASE_FIXED_COLUMNS.severity,
    maxWidth: BASE_FIXED_COLUMNS.severity,
    minWidth: BASE_FIXED_COLUMNS.severity,
    custom_order: fields.exploitation.order,
    id: 'severity',
    resizable: true,
    Cell: (rowInfo) => (
      isFetching
        ? <Skeleton />
        : (
          <Col>
            <div className={ `background-${rowInfo.original.exploitation === 'info' ? 'informational' : (rowInfo.original.exploitation === 'med' ? 'medium' : rowInfo.original.exploitation)}` }>
              <div className="severity-letter">{rowInfo.original.exploitation ? rowInfo.original.exploitation.charAt(0).toUpperCase() : ''}</div>
            </div>
          </Col>
          )
    ),
    getProps: () => ({ 'column-type': 'exploitation', 'style': { display: 'flex', justifyContent: 'center', alignItems: 'center' } })
  }, {
    Header: () => <FormattedMessage id="Name" />,
    id: 'name',
    resizable: true,
    show: fields.name.visible,
    width: widths.name,
    maxWidth: fields.name.maxWidth,
    minWidth: fields.name.minWidth,
    custom_order: fields.name.order,
    Cell: (rowInfo) => (isFetching ? <Skeleton /> : <NameCell rowInfo={ rowInfo.original } />),
    getProps: () => ({ 'column-type': 'name' })
  }, {
    Header: () => <FormattedMessage id="description" />,
    id: 'description',
    resizable: true,
    show: fields.description.visible,
    width: widths.description,
    maxWidth: fields.description.maxWidth,
    minWidth: fields.description.minWidth,
    custom_order: fields.description.order,
    Cell: (rowInfo) => (
      isFetching
        ? <Skeleton />
        : (
          <ColKbDescription>
            { rowInfo.original.description }
          </ColKbDescription>
          )
    ),
    getProps: () => ({ 'column-type': 'description' })
  }, {
    Header: () => <FormattedMessage id="references" />,
    id: 'references',
    resizable: true,
    show: fields.references.visible,
    width: widths.references,
    maxWidth: fields.references.maxWidth,
    minWidth: fields.references.minWidth,
    custom_order: fields.references.order,
    Cell: (rowInfo) => (
      isFetching
        ? <Skeleton />
        : (
          <ColKbReferences>
            { rowInfo.original.references }
          </ColKbReferences>
          )
    ),
    getProps: () => ({ 'column-type': 'references' })
  }, {
    Header: () => <FormattedMessage id="resolution" />,
    id: 'resolution',
    resizable: true,
    show: fields.resolution.visible,
    width: widths.resolution,
    maxWidth: fields.resolution.maxWidth,
    minWidth: fields.resolution.minWidth,
    custom_order: fields.resolution.order,
    Cell: (rowInfo) => (
      isFetching
        ? <Skeleton />
        : (
          <ColKbResolution>
            { rowInfo.original.resolution }
          </ColKbResolution>
          )
    ),
    getProps: () => ({ 'column-type': 'resolution' })
  }, {
    Header: () => <FormattedMessage id="policyviolations" />,
    id: 'policyviolations',
    resizable: true,
    sortable: false,
    show: fields.policyviolations.visible,
    width: widths.policyviolations,
    maxWidth: fields.policyviolations.maxWidth,
    minWidth: fields.policyviolations.minWidth,
    custom_order: fields.policyviolations.order,
    Cell: (rowInfo) => (
      isFetching
        ? <Skeleton />
        : (
          <ColKbPolicyViolations>
            { rowInfo.original.policyviolations?.map((policy, index) => {
              const { policyviolations } = rowInfo.original;
              if (policyviolations?.length > 1 && index + 1 < policyviolations?.length) return `${policy}, `;
              return `${policy}`;
            }) }
          </ColKbPolicyViolations>
          )
    ),
    getProps: () => ({ 'column-type': 'creator' })
  }, {
    Header: () => <FormattedMessage id="creator" />,
    id: 'creator__username',
    resizable: true,
    show: fields.creator_id.visible,
    width: getDefaultWidth(fields.creator_id, 100),
    maxWidth: fields.creator_id.maxWidth,
    minWidth: fields.creator_id.minWidth,
    custom_order: fields.creator_id.order,
    Cell: (rowInfo) => (
      isFetching
        ? <Skeleton />
        : (
          <ColKbCreator>
            { rowInfo.original.creator }
          </ColKbCreator>
          )
    ),
    getProps: () => ({ 'column-type': 'creator' })
  }, {
    Header: () => <FormattedMessage id="created_date" />,
    id: 'create_date',
    resizable: true,
    show: fields.create_date.visible,
    width: getDefaultWidth(fields.create_date, 170),
    maxWidth: fields.create_date.maxWidth,
    minWidth: fields.create_date.minWidth,
    custom_order: fields.create_date.order,
    Cell: (rowInfo) => (
      isFetching
        ? <Skeleton />
        : (
          <ColKbCreatedDate>
            { rowInfo.original.create_at ? <DateFormatter date={ rowInfo.original.create_at } /> : '-'}
          </ColKbCreatedDate>
          )
    ),
    getProps: () => ({ 'column-type': 'created_date' })
  }];

  const ordered_columns = columns
    .map(column => ({
      ...column,
      // Using !column.custom_order could cause problems due to how javascript handles false values. For example, if custom_order is 0
      custom_order: typeof column.custom_order === 'undefined' ? 0 : column.custom_order
    }))
    .filter((row, index) => row.show === true || index === 0)
    .sort((a, b) => a.custom_order - b.custom_order);
  return ordered_columns;
}
